import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import logoIcon from '../../assets/logo-icon.png'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Card from '../../components/common/Card'
import { login, logout } from '../../store/slices/authSlice'
import { authApi } from '../../services/api'

type View = 'email' | 'otp'

const AdminLoginPage: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [view, setView] = useState<View>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    setLoading(true)
    setError('')
    try {
      await authApi.sendOtp(email)
      setView('otp')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!otp || otp.length < 6) return
    setLoading(true)
    setError('')
    const result = await dispatch(login({ email, otp_code: otp }) as any)
    setLoading(false)
    if (login.fulfilled.match(result)) {
      const user = result.payload?.user
      if (!user?.is_admin) {
        dispatch(logout() as any)
        setError('Access denied. Administrator privileges required.')
        setView('email')
        setOtp('')
      } else {
        navigate('/admin')
      }
    } else {
      const errorMsg = result.payload as string || 'Invalid OTP. Please try again.'
      setError(errorMsg)
      if (errorMsg.toLowerCase().includes('expired')) {
        setView('email')
        setOtp('')
      }
    }
  }

  const handleResend = async () => {
    setResending(true)
    setError('')
    setResent(false)
    try {
      await authApi.sendOtp(email)
      setResent(true)
      setOtp('')
    } catch (err: any) {
      setError('Failed to resend OTP.')
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-950 py-12 px-4">
      <div className="max-w-md w-full space-y-6">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center justify-center space-x-3 group mb-4">
            <div className="relative">
              <img
                src={logoIcon}
                alt="AnalyzerView"
                className="w-14 h-14 object-contain group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="flex flex-col items-start">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                AnalyzerView
              </h1>
              <p className="text-xs text-gray-400 font-medium tracking-wide">ADMINISTRATOR</p>
            </div>
          </Link>
          <h2 className="text-2xl font-bold text-gray-100">Admin Login</h2>
          <p className="mt-2 text-sm text-red-400 font-medium">
            Restricted access. Authorized personnel only.
          </p>
        </div>

        <Card>
          {view === 'email' ? (
            <form onSubmit={handleSendOtp} className="space-y-5">
              {error && (
                <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}
              <Input
                label="Admin Email Address"
                type="email"
                name="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError('') }}
                placeholder="admin@example.com"
                required
                autoFocus
              />
              <Button type="submit" loading={loading} className="w-full" size="lg">
                Send OTP
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-100 mb-1">Check your email</h3>
                <p className="text-sm text-gray-400">
                  We sent a verification code to <span className="font-medium text-gray-200">{email}</span>
                </p>
                {resent && <p className="text-xs text-green-400 mt-2">A new code has been sent to your email.</p>}
              </div>

              {error && (
                <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Verification Code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={otp}
                  onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setError('') }}
                  placeholder="• • • • • •"
                  className="w-full bg-dark-800 border border-dark-600 text-gray-100 rounded-md shadow-sm px-4 py-3 text-center text-2xl tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500"
                  autoFocus
                  required
                />
              </div>

              <Button type="submit" loading={loading} disabled={otp.length < 6} className="w-full" size="lg">
                Verify &amp; Login
              </Button>

              <div className="flex items-center justify-between text-sm mt-4">
                <button
                  type="button"
                  onClick={() => { setView('email'); setOtp(''); setError('') }}
                  className="text-gray-400 hover:text-gray-300 font-medium transition-colors"
                >
                  Change email
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="text-primary-400 hover:text-primary-300 font-medium transition-colors disabled:opacity-50"
                >
                  {resending ? 'Sending...' : 'Resend code'}
                </button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  )
}

export default AdminLoginPage
